import React from 'react';
import useInView from '../../hooks/useInView';

/**
 * Max-width centered content wrapper.
 */
export function Container({ className = '', children }) {
  return (
    <div className={`mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Small eyebrow / section label.
 * tone: 'dark' (on light bg) | 'light' (on dark bg)
 */
export function SectionLabel({ children, tone = 'dark', className = '' }) {
  const tones = {
    dark: 'text-accent-400 bg-accent/10 ring-accent/20',
    light: 'text-accent-400 bg-white/5 ring-white/10',
  };
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ring-1 ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * Primary / secondary / ghost CTA button. Renders an <a> when `href` is set.
 * variant: 'primary' | 'secondary' | 'ghost'
 */
export function Button({
  children,
  variant = 'primary',
  href,
  onClick,
  className = '',
  external = false,
  ...rest
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold transition-all duration-200 px-5 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50';
  const variants = {
    primary:
      'bg-cta text-ink shadow-[0_8px_30px_rgba(255,176,0,0.28)] hover:bg-cta-hover hover:shadow-[0_10px_40px_rgba(255,176,0,0.45)] hover:-translate-y-0.5',
    secondary:
      'bg-white/5 text-white ring-1 ring-white/15 hover:bg-white/10 hover:ring-white/25',
    'secondary-dark':
      'bg-white/5 text-white ring-1 ring-white/10 hover:bg-white/10',
    ghost: 'text-white/80 hover:text-white',
  };
  const cls = `${base} ${variants[variant] || variants.primary} ${className}`;

  if (href) {
    return (
      <a
        href={href}
        className={cls}
        onClick={onClick}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...rest}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick} {...rest}>
      {children}
    </button>
  );
}

/**
 * Scroll-reveal wrapper. Adds fade-up when scrolled into view.
 */
export function Reveal({ children, delay = 0, className = '', as: Tag = 'div' }) {
  const { ref, isVisible } = useInView();
  return (
    <Tag
      ref={ref}
      className={`reveal ${isVisible ? 'is-visible' : ''} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}
